import React, { useState, useEffect } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { ScrollArea } from './ui/scroll-area';
import { Loader2, BookOpen, FileText, Info } from 'lucide-react';
import {
  getTafsirByAyah,
  getAllTafsirByAyah,
  formatTafsirTypeName,
  isTafsirAvailable
} from '../services/tafsirService';
import WordByWordCard from './WordByWordCard';
import AsbabunNuzulCard from './AsbabunNuzulCard';

const TafsirPanel = ({ surahNumber, ayahNumber, surahName }) => {
  const [loading, setLoading] = useState(false);
  const [tafsirs, setTafsirs] = useState([]);
  const [selectedTafsir, setSelectedTafsir] = useState('ibnu_katsir');
  const [wordByWord, setWordByWord] = useState(null);
  const [asbabunNuzul, setAsbabunNuzul] = useState(null);
  const [activeTab, setActiveTab] = useState('tafsir');

  useEffect(() => {
    if (surahNumber && ayahNumber) {
      fetchAllTafsirData();
    }
  }, [surahNumber, ayahNumber]);

  const fetchAllTafsirData = async () => {
    setLoading(true);
    try {
      // Fetch all tafsir types
      const tafsirData = await getAllTafsirByAyah(surahNumber, ayahNumber);
      setTafsirs(tafsirData.tafsirs || []);

      // Fetch word by word (optional - can be loaded on demand)
      // setWordByWord(await getWordByWord(surahNumber, ayahNumber));

      // Fetch asbabun nuzul (optional - can be loaded on demand)
      // setAsbabunNuzul(await getAsbabunNuzulAyah(surahNumber, ayahNumber));

    } catch (error) {
      console.error('Error fetching tafsir data:', error);
    } finally {
      setLoading(false);
    }
  };

  const currentTafsir = tafsirs.find(t => t.tafsir_type === selectedTafsir);

  if (loading) {
    return (
      <Card className="w-full">
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
          <span className="ml-2 text-muted-foreground">Memuat tafsir...</span>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <BookOpen className="h-5 w-5 text-primary" />
          Tafsir Interaktif
        </CardTitle>
        <CardDescription>
          {surahName} - Ayat {ayahNumber}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="tafsir">Tafsir</TabsTrigger>
            <TabsTrigger value="word-by-word">Kata per Kata</TabsTrigger>
            <TabsTrigger value="asbabun-nuzul">Asbabun Nuzul</TabsTrigger>
          </TabsList>

          {/* TAFSIR TAB */}
          <TabsContent value="tafsir" className="mt-4">
            {tafsirs.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Info className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>Maaf, tafsir belum tersedia untuk ayat ini.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Tafsir Type Selector */}
                {tafsirs.length > 1 && (
                  <div className="flex gap-2 flex-wrap">
                    {tafsirs.map((tafsir) => (
                      <Badge
                        key={tafsir.tafsir_type}
                        variant={selectedTafsir === tafsir.tafsir_type ? "default" : "outline"}
                        className="cursor-pointer"
                        onClick={() => setSelectedTafsir(tafsir.tafsir_type)}
                      >
                        {formatTafsirTypeName(tafsir.tafsir_type)}
                      </Badge>
                    ))}
                  </div>
                )}

                {currentTafsir && (
                  <ScrollArea className="h-[400px] w-full rounded-md border p-4">
                    <div className="space-y-4">
                      {currentTafsir.text_arabic && (
                        <div className="text-right">
                          <p className="text-2xl leading-loose font-arabic" dir="rtl">
                            {currentTafsir.text_arabic}
                          </p>
                          <Separator className="my-4" />
                        </div>
                      )}

                      <div>
                        <h4 className="font-semibold mb-2 flex items-center gap-2">
                          <FileText className="h-4 w-4" />
                          {formatTafsirTypeName(currentTafsir.tafsir_type)}
                        </h4>
                        <p className="text-sm leading-relaxed whitespace-pre-wrap">
                          {currentTafsir.text_indonesian}
                        </p>
                      </div>
                    </div>
                  </ScrollArea>
                )}
              </div>
            )}
          </TabsContent>

          {/* WORD BY WORD TAB */}
          <TabsContent value="word-by-word" className="mt-4">
            <WordByWordCard surahNumber={surahNumber} ayahNumber={ayahNumber} />
          </TabsContent>

          {/* ASBABUN NUZUL TAB */}
          <TabsContent value="asbabun-nuzul" className="mt-4">
            <AsbabunNuzulCard surahNumber={surahNumber} ayahNumber={ayahNumber} />
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default TafsirPanel;
